import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCategories() {
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: '', slug: '', icon: '', description: '', order: 99 });

    const load = async () => { const { data } = await api.get('/admin/categories'); setCats(data); };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        try {
            if (editing) await api.put(`/admin/categories/${editing.id}`, form);
            else await api.post('/admin/categories', form);
            toast.success('Guardado');
            setEditing(null); setCreating(false); setForm({ name: '', slug: '', icon: '', description: '', order: 99 });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const openCreate = () => { setForm({ name: '', slug: '', icon: '', description: '', order: cats.length + 1 }); setCreating(true); setEditing(null); };
    const openEdit = (c) => { setForm({ name: c.name, slug: c.slug, icon: c.icon || '', description: c.description || '', order: c.order, active: c.active }); setEditing(c); setCreating(false); };

    const toggleActive = async (c) => { await api.put(`/admin/categories/${c.id}`, { active: !c.active }); load(); };
    const del = async (id) => { if (!window.confirm('¿Eliminar categoría?')) return; await api.delete(`/admin/categories/${id}`); toast.success('Eliminada'); load(); };

    return (
        <div className="space-y-4">
            <div className="flex items-baseline justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Categorías</h1>
                    <p className="text-sm text-muted-foreground">Gestiona las categorías del marketplace.</p>
                </div>
                <Button onClick={openCreate} data-testid="category-new-btn"><Plus className="h-4 w-4 mr-1" />Nueva categoría</Button>
            </div>

            <div className="rounded-xl border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground text-left border-b"><tr>
                        <th className="p-3">Orden</th><th>Nombre</th><th>Slug</th><th>Anuncios</th><th>Activa</th><th></th>
                    </tr></thead>
                    <tbody data-testid="admin-cats-table">
                        {cats.map(c => (
                            <tr key={c.id} className="border-b">
                                <td className="p-3 tabular-nums">{c.order}</td>
                                <td className="p-3 font-medium">{c.name}</td>
                                <td className="p-3 text-muted-foreground">{c.slug}</td>
                                <td className="p-3 tabular-nums">{c.ads_count || 0}</td>
                                <td className="p-3"><Switch checked={!!c.active} onCheckedChange={() => toggleActive(c)} /></td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => openEdit(c)}><Edit className="h-3 w-3" /></Button>
                                        <Button size="sm" variant="outline" onClick={() => del(c.id)}><Trash2 className="h-3 w-3" /></Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" /></div>
                        <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="cat-slug" /></div>
                        <div><Label>Orden</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
                        <div className="col-span-2"><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancelar</Button>
                        <Button onClick={submit} data-testid="cat-submit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
